import { ExpandingComponent, FormFieldInputNew, FormFieldSelect, FormFieldUploadFile, Span, Chevron } from "@netapp/bxp-design-system-react";
import { useTranslation } from "react-i18next";
import { useSelector } from 'react-redux';
import * as yup from "yup";
import { addConnector as i18nNamespace } from "../../../../i18n/namespace";
import { ColumnHeader } from "./Components";
import styles from './ProxyConfiguration.module.scss';

export const PROXY_TYPES = {
    EXPLICIT: "EXPLICIT",
    TRANSPARENT: "TRANSPARENT",
    NONE: "NONE"
}

export const proxyValidation = (t) => {
    return {
        proxyUri: yup
            .string()
            .when("proxyConfigurationType.value", {
                is: (proxyConfigurationType) => proxyConfigurationType === PROXY_TYPES.EXPLICIT,
                then: yup
                    .string().required(t("proxy_required"))
                    .test("is-http expression", t("proxy_must_begin_with_https_and_contain_a_valid_uri"), val => {
                        if (val < 1 || val === undefined || val === null) {
                            return true;
                        }
                        return val.match(/^((http|https):\/\/?)[^\s()<>]+(?:\([\w\d]+\)|([^[:punct]\s]|\/?))$/i);
                    })
                    .when(["proxyUsername", "proxyPassword", "certificateFile"], {
                        is: (proxyUsername, proxyPassword, certificateFile) => {
                            return proxyUsername || proxyPassword || certificateFile;
                        },
                        then: yup
                            .string()
                            .required(t("proxy_uri_is_required_if_providing_username_password_or_certificates"))
                            .matches(/^(https?):\/\//i, t("proxy_must_begin_with_https_and_contain_a_valid_uri"))
                    }),
            }),
        proxyUsername: yup
            .string()
            .when("proxyPassword", {
                is: pass => Boolean(pass),
                then: yup.string().required(t("proxy_user_name_required"))
            }),
        proxyPassword: yup
            .string()
            .when("proxyUsername", {
                is: user => Boolean(user),
                then: yup.string().required(t("proxy_password_required"))
            }),
        certificateFile: yup
            .string()
            .when("proxyConfigurationType.value", {
                is: (proxyConfigurationType) => proxyConfigurationType === PROXY_TYPES.TRANSPARENT,
                then: yup
                    .string().required(t("proxy_root_certificate_required"))
            })
    };
};

export const proxyValidationWithoutTransparent = (t) => {
    return {
        proxyUri: yup
            .string()
            .test("is-http expression", t("proxy_must_begin_with_https_and_contain_a_valid_uri"), val => {
                if (val < 1 || val === undefined || val === null) {
                    return true;
                }
                return val.match(/^((http|https):\/\/?)[^\s()<>]+(?:\([\w\d]+\)|([^[:punct]\s]|\/?))$/i);
            })
            .when(["proxyUsername", "proxyPassword", "certificateFile"], {
                is: (proxyUsername, proxyPassword, certificateFile) => {
                    return proxyUsername || proxyPassword || certificateFile;
                },
                then: yup
                    .string()
                    .required(t("proxy_uri_is_required_if_providing_username_password_or_certificates"))
                    .matches(/^(https?):\/\//i, t("proxy_must_begin_with_https_and_contain_a_valid_uri"))
            }),
        proxyUsername: yup
            .string()
            .when("proxyPassword", {
                is: pass => Boolean(pass),
                then: yup.string().required(t("proxy_user_name_required"))
            }),
        proxyPassword: yup
            .string()
            .when("proxyUsername", {
                is: user => Boolean(user),
                then: yup.string().required(t("proxy_password_required"))
            }),
    };
};

const DefineCredentialsButton = ({ isOpen }) => {
    const { t } = useTranslation(i18nNamespace);

    return <>
        <p>{t("define_credentials_for_this_proxy")}</p>
        <div className={styles['chevron-margin']}><Chevron isActive={isOpen} color="secondary" size={"sm"} /></div>
    </>;
};

const UploadCertificateButton = ({ isOpen }) => {
    const { t } = useTranslation(i18nNamespace);

    return <>
        <p>{t("upload_a_root_certificate")}</p>
        <div className={styles['chevron-margin']}><Chevron isActive={isOpen} color="secondary" size={"sm"} /></div>
    </>;
};

const ProxyConfiguration = ({ form }) => {
    const { formState, handleFormChange } = form;
    const { t } = useTranslation(i18nNamespace);
    const proxyConfigurationTypeOptions = [
        { label: t("explicit_proxy_type"), value: PROXY_TYPES.EXPLICIT },
        { label: t("transparent_proxy_type"), value: PROXY_TYPES.TRANSPARENT },
        { label: t("none_proxy_type"), value: PROXY_TYPES.NONE },
    ];    
    const proxyConfigurationType = formState.proxyConfigurationType?.value;

    const featuresByKey = useSelector(state => state.features.byKey);
    const transparentProxyFeatureEnabled = featuresByKey["NetApp.Connectors/transparentProxy"].active;

    if(!proxyConfigurationType && transparentProxyFeatureEnabled) {
        form.handleFormChange({ label: t("none_proxy_type"), value: PROXY_TYPES.NONE }, {name: "proxyConfigurationType"});
    }

    const onProxyConfigurationTypeChange = (v, e) => {
        form.handleFormChange(v, e);
        form.handleFormChange(undefined, {name:"proxyUri"});
        form.handleFormChange(undefined, {name:"proxyUsername"});
        form.handleFormChange(undefined, {name:"proxyPassword"});
        form.handleFormChange(undefined, {name:"certificateFile"});
    }

    return <>
        <ColumnHeader>{t("proxy_configuration")}<Span italic>{t("optional")}</Span></ColumnHeader>
        {transparentProxyFeatureEnabled && <FormFieldSelect label={t("proxy_configuration_type")}
            name="proxyConfigurationType"
            form={form}
            onChange={onProxyConfigurationTypeChange}
            options={proxyConfigurationTypeOptions} />}
        { (proxyConfigurationType === PROXY_TYPES.EXPLICIT || !transparentProxyFeatureEnabled)  && <> <FormFieldInputNew
            form={form}
            name="proxyUri"
            placeholder={t("proxy_placeholder_example")}
            label={t("http_proxy")}
            className={styles['proxy-input-spacing']}
        />
       <ExpandingComponent className={styles['expanding-component-spacing']} contentStyle={{ paddingTop: 28 }}
                            headerStyle={{ alignItems: "center" }} ButtonChildren={DefineCredentialsButton}>
            <FormFieldInputNew
                form={form}
                name="proxyUsername"
                label={t("username")}
                className={styles['proxy-input-spacing']}
                autoComplete="off"
            />
            <FormFieldInputNew
                form={form}
                name="proxyPassword"
                type="password"
                label={t("password")}
            />
        </ExpandingComponent> </>}
        {((proxyConfigurationType === PROXY_TYPES.EXPLICIT || proxyConfigurationType === PROXY_TYPES.TRANSPARENT) || !transparentProxyFeatureEnabled) &&
            <ExpandingComponent defaultValue={proxyConfigurationType === PROXY_TYPES.TRANSPARENT} key={proxyConfigurationType + '-cert'} className={styles['expanding-component-spacing']} contentStyle={{ paddingTop: 16 }}
                headerStyle={{ alignItems: "center" }} ButtonChildren={UploadCertificateButton}>
                <FormFieldUploadFile form={form} label={t("ca_signed_certificate")} name="certificateFile" placeholder={t("choose_a_certificate")} />
            </ExpandingComponent>}
    </>;
};

export default ProxyConfiguration;
